import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Redirect,
  generatePath,
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom";
import { Typography } from "@mui/material";

import Breadcrumbs from "../../../../../components/BreadCrumbs";
import Button from "../../../../../components/Button";
import { UITabs } from "../../../../../components/ui";
import messages from "../../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../../utils/RoutesPaths";
import { isSuperAdmin } from "../../../../../utils/Permissions";
import { appendActiveUrlTabBreadcrumb } from "../../../../../utils/Helpers";
import CompanyHeader from "../../../../../components/Company/CompanyViewDetails/Header/CompanyHeader";
import {
  getCompanyDetailsRequest,
  clearCompanyDetails,
} from "../../../../../store/RegistrationReq/actions";
import OverviewTab from "./Tabs/OverviewTab";
import CompanyClientsTab from "./Tabs/CompanyClientsTab";
import CompanyTeamPermissionsTab from "./Tabs/CompanyTeamPermissionsTab";
import CompanyActivityLogTab from "./Tabs/CompanyActivityLogTab";
import CompanyEditRequestBanner from "../../../../../components/Company/CompanyEditRequestBanner";
import { COMPANY_DETAILS_TEAM_TAB } from "../../../TeamManagement/adminCompanyTeamRoutes";

import "./CompanyDetailsPage.scss";

export const COMPANY_DETAILS_TABS = {
  overview: "overview",
  clients: "clients",
  teamPermissions: COMPANY_DETAILS_TEAM_TAB,
  logs: "logs",
};

const COMPANY_DETAILS_TAB_VALUES = Object.values(COMPANY_DETAILS_TABS);

const getTabFromSearch = (search) => {
  const tab = new URLSearchParams(search).get("tab");
  return COMPANY_DETAILS_TAB_VALUES.includes(tab) ? tab : null;
};

export function CompanyDetailsClientsTabRedirect() {
  const { id } = useParams();

  return (
    <Redirect
      to={{
        pathname: generatePath(ROUTE_PATHS.companiesListCompanyDetails, {
          id,
        }),
        search: `?tab=${COMPANY_DETAILS_TABS.clients}`,
      }}
    />
  );
}

const CompanyDetailsPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const companyDetails = useSelector(
    (state) => state.companiesRegistration.companyDetails,
  );
  const companyDetailsRequestId = useSelector(
    (state) => state.companiesRegistration.companyDetailsRequestId,
  );
  const companyDetailsLoading = useSelector(
    (state) => state.companiesRegistration.companyDetailsLoading,
  );
  const companyDetailsError = useSelector(
    (state) => state.companiesRegistration.companyDetailsError,
  );
  const {
    registrationReq,
    companiesList: cl,
    profileEditRequests: per,
  } = messages[lang];
  const fromProfileEditRequests = Boolean(
    location.state?.fromProfileEditRequests,
  );
  const companyDetailsPath = generatePath(ROUTE_PATHS.companiesListCompanyDetails, {
    id,
  });
  const activeTab = useMemo(
    () => getTabFromSearch(location.search) ?? COMPANY_DETAILS_TABS.overview,
    [location.search],
  );

  useEffect(() => {
    if (getTabFromSearch(location.search)) return;

    const params = new URLSearchParams(location.search);
    params.set("tab", COMPANY_DETAILS_TABS.overview);
    history.replace({
      pathname: companyDetailsPath,
      search: `?${params.toString()}`,
    });
  }, [companyDetailsPath, history, location.search]);

  useEffect(() => {
    if (location.state?.activeTab !== COMPANY_DETAILS_TABS.clients) return;

    history.replace({
      pathname: companyDetailsPath,
      search: `?tab=${COMPANY_DETAILS_TABS.clients}`,
    });
  }, [companyDetailsPath, history, location.state?.activeTab]);

  const handleTabChange = (nextTab) => {
    if (nextTab === activeTab) return;

    const params = new URLSearchParams();
    params.set("tab", nextTab);
    if (nextTab === COMPANY_DETAILS_TABS.teamPermissions) {
      params.set("subtab", "employees");
    }
    history.push({
      pathname: companyDetailsPath,
      search: `?${params.toString()}`,
    });
  };

  // Refetch when `lang` changes so axios interceptor sends matching `locale` query (backend i18n).
  useEffect(() => {
    if (id) {
      dispatch(getCompanyDetailsRequest({ id, force: true }));
    }
    return () => dispatch(clearCompanyDetails());
  }, [dispatch, id, lang]);

  const companyTabLabels = useMemo(
    () => ({
      [COMPANY_DETAILS_TABS.overview]: cl.overviewTab,
      [COMPANY_DETAILS_TABS.teamPermissions]: cl.teamPermissionsTab,
      [COMPANY_DETAILS_TABS.clients]: cl.clientsTab,
      [COMPANY_DETAILS_TABS.logs]: cl.logsTab,
    }),
    [cl.clientsTab, cl.logsTab, cl.overviewTab, cl.teamPermissionsTab],
  );

  const breadcrumbList = useMemo(
    () =>
      appendActiveUrlTabBreadcrumb({
        breadcrumbs: [
          fromProfileEditRequests
            ? { path: ROUTE_PATHS.profileEditRequests, label: per.listBreadcrumb }
            : { path: ROUTE_PATHS.companiesList, label: cl.listBreadcrumb },
          {
            path: "",
            label: companyDetails?.name_en || cl.companyProfile,
          },
        ],
        activeTab,
        defaultTab: COMPANY_DETAILS_TABS.overview,
        tabLabelsByValue: companyTabLabels,
        viewPathname: companyDetailsPath,
        search: location.search,
      }),
    [
      activeTab,
      cl.companyProfile,
      cl.listBreadcrumb,
      companyDetails?.name_en,
      companyDetailsPath,
      companyTabLabels,
      fromProfileEditRequests,
      location.search,
      per.listBreadcrumb,
    ],
  );

  if (!isSuperAdmin()) {
    return <Redirect to={ROUTE_PATHS.events} />;
  }

  const mismatched =
    companyDetails &&
    companyDetailsRequestId &&
    String(companyDetailsRequestId) !== String(id);

  if (companyDetailsError) {
    return (
      <div className="registration-req-page">
        <div className="mt-2">
          <Breadcrumbs
            list={breadcrumbList}
            BreadCrumbHasBackIcon={false}
            className="mb-3"
          />
        </div>
        <div className="py-4 text-center">
          <Typography color="error" className="mb-3">
            {companyDetailsError || registrationReq.companyDetailsLoadError}
          </Typography>
          <Button
            label={registrationReq.retry}
            onClick={() =>
              dispatch(getCompanyDetailsRequest({ id, force: true }))
            }
          />
        </div>
      </div>
    );
  }

  if ((companyDetailsLoading && !companyDetails) || mismatched) {
    return (
      <div className="registration-req-page">
        <div className="mt-2">
          <Breadcrumbs
            list={breadcrumbList}
            BreadCrumbHasBackIcon={false}
            className="mb-3"
          />
        </div>
      </div>
    );
  }

  if (!companyDetails) {
    return null;
  }

  const tabs = [
    {
      value: COMPANY_DETAILS_TABS.overview,
      label: cl.overviewTab,
    },
    {
      value: COMPANY_DETAILS_TABS.teamPermissions,
      label: cl.teamPermissionsTab,
    },
    {
      value: COMPANY_DETAILS_TABS.clients,
      label: cl.clientsTab,
    },
    {
      value: COMPANY_DETAILS_TABS.logs,
      label: cl.logsTab,
    },
  ];

  const handleEditProfile = () => {
    history.push(
      generatePath(ROUTE_PATHS.companiesListCompanyEdit, {
        id,
      }),
    );
  };

  return (
    <>
      <div className="registration-req-page">
        <div className="mt-2">
          <Breadcrumbs
            list={breadcrumbList}
            BreadCrumbHasBackIcon={false}
            className="mb-3"
          />
        </div>
        <CompanyHeader
          company={companyDetails}
          hasJoinedDate
          onEditProfile={handleEditProfile}
        />
        {companyDetails.company_edit_request && <CompanyEditRequestBanner />}
        <div className="company-view-details company-details-page--companies-list">
          <UITabs
            tabs={tabs}
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons={false}
            classNames={{ root: "company-details-page__tabs" }}
            rootSx={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-20)",
            }}
          >
            {activeTab === COMPANY_DETAILS_TABS.overview && (
              <OverviewTab
                company={companyDetails}
                lang={lang}
                registrationMessages={registrationReq}
                companiesListMessages={cl}
              />
            )}
            {activeTab === COMPANY_DETAILS_TABS.clients && (
              <CompanyClientsTab companyId={id} />
            )}
            {activeTab === COMPANY_DETAILS_TABS.teamPermissions && (
              <CompanyTeamPermissionsTab companyId={id} />
            )}
            {activeTab === COMPANY_DETAILS_TABS.logs && (
              <CompanyActivityLogTab companyId={id} />
            )}
          </UITabs>
        </div>
      </div>
    </>
  );
};

export default CompanyDetailsPage;
